import type { StorefrontLanguage } from './index';

type LocalizedProductLanguage = Exclude<StorefrontLanguage, 'en'>;

interface LocalizedProductCopy {
  title: string;
  descriptionBlocks: string[];
}

interface ProductTranslation {
  sourceTitle: string;
  fr: LocalizedProductCopy;
  ar: LocalizedProductCopy;
}

export interface RemoteProductTranslation {
  handle: string;
  sourceTitle: string;
  frTitle: string;
  frDescription: string;
  arTitle: string;
  arDescription: string;
}

const productTranslations: Record<string, ProductTranslation> = {
  'the-garden-grid-edit': {
    sourceTitle: 'The Garden Grid Edit',
    fr: {
      title: 'Édition Jardin graphique',
      descriptionBlocks: [
        'Édition Jardin graphique',
        'Un petit jardin, joliment encadré. 💙🌸',
        'Le coton bleu canard rencontre un délicat travail floral fait main et de charmants carreaux, pour une rida fraîche, élégante et résolument chic.',
        'Pour les journées où vous souhaitez allier confort et touche artisanale. ✨',
        'Tissu principal : coton doux',
        'Couleur : bleu canard',
      ],
    },
    ar: {
      title: 'إصدار الحديقة المنسّقة',
      descriptionBlocks: [
        'إصدار الحديقة المنسّقة',
        'حديقة صغيرة مؤطّرة بجمال. 💙🌸',
        'يلتقي القطن الناعم باللون الأزرق المخضر مع تطريزات زهرية يدوية ولمسات مربعة أنيقة، لتمنح ريدا إطلالة منعشة وراقية وعصرية.',
        'لأيام تبحثين فيها عن الراحة مع لمسة من الفن. ✨',
        'القماش الأساسي: قطن ناعم',
        'اللون: أزرق مخضر',
      ],
    },
  },
  'modern-muse-edit-2': {
    sourceTitle: 'Modern Muse Edit 2',
    fr: {
      title: 'Édition Muse moderne 2',
      descriptionBlocks: [
        'Douces, sophistiquées et naturellement chics, nos ridas au style jupe sont pensées pour votre prochaine escapade. 🤍',
        'Un tissu au toucher velouté, un imprimé discret inspiré du monogramme et de délicats boutons floraux brodés à la main, le tout souligné par de beaux contrastes.',
        'Un esprit vacances, avec toute l’élégance d’une rida. 🌸✨',
      ],
    },
    ar: {
      title: 'إصدار الملهمة العصرية 2',
      descriptionBlocks: [
        'ناعمة وراقية وأنيقة بلا تكلّف؛ صُمّمت ريداتنا بقصّة التنورة لترافقك في عطلتك المقبلة. 🤍',
        'قماش بملمس مخملي، ونقشة هادئة مستوحاة من المونوغرام، وأزرار زهرية مطرّزة يدويًا، مع تفاصيل متباينة أنيقة.',
        'إطلالة عطلة بروح ريدا. 🌸✨',
      ],
    },
  },
  'modern-muse-edit': {
    sourceTitle: 'Modern Muse Edit',
    fr: {
      title: 'Édition Muse moderne',
      descriptionBlocks: [
        'La deuxième rida à jupe cousue de cette série, ornée de petits boutons fleuris façon tournesol.',
        'Douces, sophistiquées et naturellement chics, nos ridas au style jupe sont pensées pour votre prochaine escapade. 🤍',
        'Un tissu au toucher velouté, un imprimé discret inspiré du monogramme et de délicats boutons floraux brodés à la main, le tout souligné par de beaux contrastes.',
        'Un esprit vacances, avec toute l’élégance d’une rida. 🌸',
      ],
    },
    ar: {
      title: 'إصدار الملهمة العصرية',
      descriptionBlocks: [
        'الريدا الثانية ذات التنورة المخيطة في هذه المجموعة، وتزيّن أزرارها زهور صغيرة مستوحاة من دوّار الشمس.',
        'ناعمة وراقية وأنيقة بلا تكلّف؛ صُمّمت ريداتنا بقصّة التنورة لترافقك في عطلتك المقبلة. 🤍',
        'قماش بملمس مخملي، ونقشة هادئة مستوحاة من المونوغرام، وأزرار زهرية مطرّزة يدويًا، مع تفاصيل متباينة أنيقة.',
        'إطلالة عطلة بروح ريدا. 🌸',
      ],
    },
  },
  'the-gingham-edit-checkered-pardi': {
    sourceTitle: 'The Gingham Edit - Checkered Pardi',
    fr: {
      title: 'Édition vichy – Pardi à carreaux',
      descriptionBlocks: [
        'Une rida à jupe en vichy bleu marine, ornée de délicats motifs de cerises, d’une broderie anglaise blanche raffinée et d’une jolie dentelle festonnée — à la fois espiègle et élégante. ✨',
        'Pour les jours où vous voulez que votre rida soit aussi jolie que votre humeur. 💙',
      ],
    },
    ar: {
      title: 'إصدار الفيشِي – باردي مربّع',
      descriptionBlocks: [
        'ريدا بقصّة تنورة ونقشة فيشي باللون الكحلي، تزيّنها حبات كرز رقيقة وتطريزات إنجليزية بيضاء دقيقة ودانتيل جميل بحواف متموّجة؛ مرحة وأنيقة في آنٍ واحد. ✨',
        'لأيام ترغبين فيها أن تعكس ريداك جمال مزاجك. 💙',
      ],
    },
  },
  'the-gingham-edit-hakoba-pardi': {
    sourceTitle: 'The Gingham Edit - Hakoba Pardi',
    fr: {
      title: 'Édition vichy – Pardi Hakoba',
      descriptionBlocks: [
        'Une rida à jupe en vichy bleu marine, ornée de délicats motifs de cerises, d’une broderie anglaise blanche raffinée et d’une jolie dentelle — à la fois espiègle et élégante. ✨',
        'Pour les jours où vous voulez que votre rida soit aussi jolie que votre humeur. 💙',
      ],
    },
    ar: {
      title: 'إصدار الفيشِي – باردي هاكوبا',
      descriptionBlocks: [
        'ريدا بقصّة تنورة ونقشة فيشي باللون الكحلي، تزيّنها حبات كرز رقيقة وتطريزات إنجليزية بيضاء دقيقة ودانتيل جميل؛ مرحة وأنيقة في آنٍ واحد. ✨',
        'لأيام ترغبين فيها أن تعكس ريداك جمال مزاجك. 💙',
      ],
    },
  },
  'a-maroon-affair': {
    sourceTitle: 'A Maroon affair',
    fr: {
      title: 'Un air de bordeaux',
      descriptionBlocks: [],
    },
    ar: {
      title: 'حكاية باللون العنابي',
      descriptionBlocks: [],
    },
  },
  'ivory-elegance': {
    sourceTitle: 'Ivory Elegance',
    fr: {
      title: 'Élégance ivoire',
      descriptionBlocks: [
        'Une rida en organza couleur perle, blanc cassé, rehaussée d’un panneau floral orné de tikris et de moti.',
        'Un riche galon de cordon turc, travaillé à la main, encadre les deux côtés. Cette bordure finement ouvragée associe le zari, des nœuds français et des motifs brodés de sequins et de fils.',
        'Tissu principal : organza couleur perle',
      ],
    },
    ar: {
      title: 'أناقة العاج',
      descriptionBlocks: [
        'ريدا من أورغانزا بلون اللؤلؤ العاجي، تتوسطها لوحة زهرية تزيّنها تفاصيل تيكري وموتي.',
        'يحيط بها شريط تركي سميك من الحبال، مزخرف يدويًا من الأمام والخلف. صُنعت حوافه بدقة باستخدام الزري والعُقد الفرنسية وزخارف من الترتر والخيوط.',
        'القماش الأساسي: أورغانزا بلون اللؤلؤ',
      ],
    },
  },
  'the-silver-blush': {
    sourceTitle: 'The Silver Blush',
    fr: {
      title: 'Douceur argentée',
      descriptionBlocks: [
        'Une adorable rida bleu poudré, ornée de broderies à l’avant comme au dos. Les motifs mêlent sequins, perles et éléments en cutmoti, dans des tons de rose, d’argent et d’or.',
        'Tissu principal : double organza (organza de soie)',
      ],
    },
    ar: {
      title: 'لمسة فضية ناعمة',
      descriptionBlocks: [
        'ريدا جميلة باللون الأزرق البودري، مزخرفة من الأمام والخلف. تجمع الزخارف بين الترتر واللؤلؤ وقطع كتموتي البارزة بدرجات الوردي والفضي والذهبي.',
        'القماش الأساسي: أورغانزا مزدوجة (أورغانزا حريرية)',
      ],
    },
  },
  'the-lemonade-edit': {
    sourceTitle: 'The Lemonade Edit',
    fr: {
      title: 'Édition Limonade',
      descriptionBlocks: [
        'Une rida en coton doux à fines rayures, brodée de petits citrons et rehaussée de volants jaune citron en kali. Cette rida imprimée est proposée à 1 950 ₹.',
        'Cousue dans un style jupe, elle présente une longueur finie de 40,5.',
      ],
    },
    ar: {
      title: 'إصدار الليمونادة',
      descriptionBlocks: [
        'ريدا من القطن الناعم بخطوط رفيعة، تتناثر عليها حبات ليمون مطرّزة وتزيّنها كشكشة بلون الليمون الأصفر بأسلوب كالي. سعر هذه الريدا المنقوشة ١٬٩٥٠ روبية.',
        'مخيطة بقصّة تنورة، بطول نهائي جاهز قدره ٤٠٫٥.',
      ],
    },
  },
  'teal-bloom-kali-rida': {
    sourceTitle: 'Teal Bloom Kali Rida',
    fr: {
      title: 'Rida Kali floraison bleu canard',
      descriptionBlocks: [
        'Une rida Kali en soie mexicaine bleu canard, ornée de bouquets de broderie aari sur toute sa surface. De petits latkans viennent parfaire son allure.',
      ],
    },
    ar: {
      title: 'ريدا كالي بزهور زرقاء مخضرّة',
      descriptionBlocks: [
        'ريدا كالي من الحرير المكسيكي باللون الأزرق المخضر، تزيّنها باقات من تطريز آري في أنحاء القطعة. وتضيف تعليقات لاتكان الصغيرة لمسة جميلة إلى إطلالتها.',
      ],
    },
  },
  'sunflower-hand-painted-rida': {
    sourceTitle: 'Sunflower hand-painted rida',
    fr: {
      title: 'Rida tournesols peints à la main',
      descriptionBlocks: [
        '🌻 Un rayon de soleil dans chaque arabesque. 💙',
        'Une rida bleu ciel légère, illuminée de tournesols peints à la main, de gracieux motifs de lianes et de délicates broderies — joyeuse, féminine et prête à égayer chaque occasion. ✨',
        'Édition Grâce ensoleillée',
        'Tissu : soie modal',
        'Détails : tournesols peints à la main et broderies artisanales',
        'Finition : délicat bord perlé',
        'Un peu de soleil, où que vous alliez. 🌼',
      ],
    },
    ar: {
      title: 'ريدا مزينة بزهور دوّار الشمس المرسومة يدويًا',
      descriptionBlocks: [
        '🌻 شمسٌ تنسج حكايتها في كل التفافة. 💙',
        'ريدا خفيفة باللون الأزرق السماوي، تزيّنها زهور دوّار الشمس المرسومة يدويًا وأغصان رشيقة وتطريزات دقيقة؛ مبهجة وأنثوية لتضفي إشراقة على كل مناسبة. ✨',
        'إصدار إشراقة الشمس',
        'القماش: حرير مودال',
        'التفاصيل: زهور دوّار الشمس مرسومة يدويًا مع تطريزات يدوية',
        'اللمسة النهائية: حافة رقيقة مزينة باللؤلؤ',
        'قليل من الشمس أينما ذهبتِ. 🌼',
      ],
    },
  },
  'maa-ki-dua': {
    sourceTitle: 'Maa ki Dua',
    fr: {
      title: 'La prière de maman',
      descriptionBlocks: [
        'La prière de maman 🤍✨',
        'La troisième rida de notre série spéciale Mariage ivoire, pensée pour celle dont l’amour, la force et les prières rassemblent toute la famille. 🌸',
        'Une élégante rida ivoire aux accents de rose tendre et de magenta profond, ornée de délicates broderies florales et de dentelle festonnée — une pièce créée avec amour pour Mami, la mère du marié. ❤️',
        'La prière de maman — nos prières, notre commencement. ✨',
      ],
    },
    ar: {
      title: 'دعاء الأم',
      descriptionBlocks: [
        'دعاء الأم 🤍✨',
        'الريدا الثالثة من مجموعتنا الخاصة لزفاف العاج، صُمّمت للمرأة التي يجمع حبها وقوتها ودعاؤها شمل العائلة. 🌸',
        'ريدا أنيقة بلون العاج مع لمسات من الوردي الناعم والفوشيا الداكن، وتطريزات زهرية رقيقة ودانتيل بحواف متموّجة؛ قطعة صُنعت بمحبة لمامي، أمّ العريس. ❤️',
        'دعاء الأم — دعاؤها هو بدايتنا. ✨',
      ],
    },
  },
  'svarovski-organza-edit': {
    sourceTitle: 'Svarovski Organza Edit',
    fr: {
      title: 'Édition Organza Svarovski',
      descriptionBlocks: [
        '✨ Un peu d’éclat. Beaucoup d’élégance. ✨',
        'Découvrez notre Édition Organza Svarovski : trois nuances ombrées intemporelles, pensées pour celles qui aiment une grâce naturelle. Un organza délicat, de magnifiques ornements faits main et une silhouette aussi belle à porter qu’à regarder.',
        'Quantités limitées. Une fois les pièces épuisées, elles ne reviendront pas.',
        'Organza, ombré, collection de ridas, rida de créateur, broderie artisanale, perles, ornements en perles',
      ],
    },
    ar: {
      title: 'إصدار أورغانزا سفاروفسكي',
      descriptionBlocks: [
        '✨ قليل من البريق. كثير من الأناقة. ✨',
        'اكتشفي إصدار أورغانزا سفاروفسكي: ثلاث درجات متدرّجة خالدة، صُمّمت للمرأة التي تحب الرقيّ السهل. أورغانزا رقيقة، وزخارف يدوية متقنة، وقصّة جميلة بقدر ما هي مريحة.',
        'القطع محدودة. وعند نفادها لن تتوفر مجددًا.',
        'أورغانزا، تدرّجات لونية، مجموعة ريدات، ريدا بتصميم خاص، تطريز يدوي، لآلئ، زينة من اللؤلؤ',
      ],
    },
  },
  'custom-rida-request': {
    sourceTitle: 'Custom Rida Request',
    fr: {
      title: 'Demande de rida personnalisée',
      descriptionBlocks: [
        'Partagez votre inspiration et les détails de personnalisation. Notre atelier étudiera votre demande et vous guidera pour la suite.',
      ],
    },
    ar: {
      title: 'طلب ريدا مخصّصة',
      descriptionBlocks: [
        'شاركي مصدر إلهامك وتفاصيل التخصيص. سيراجع مشغلنا طلبك ويرشدك إلى الخطوات التالية.',
      ],
    },
  },
};

const translationsBySourceTitle = new Map(
  Object.values(productTranslations).map((translation) => [
    translation.sourceTitle,
    translation,
  ]),
);
let remoteTranslationsByHandle = new Map<string, ProductTranslation>();
let remoteTranslationsBySourceTitle = new Map<string, ProductTranslation>();
const warnedMissingTranslations = new Set<string>();

export function setRemoteProductTranslations(
  translations: RemoteProductTranslation[],
) {
  remoteTranslationsByHandle = new Map(
    translations.map((translation) => [
      translation.handle,
      {
        sourceTitle: translation.sourceTitle,
        fr: {
          title: translation.frTitle,
          descriptionBlocks: splitDescription(translation.frDescription),
        },
        ar: {
          title: translation.arTitle,
          descriptionBlocks: splitDescription(translation.arDescription),
        },
      },
    ]),
  );
  remoteTranslationsBySourceTitle = new Map(
    translations.map((translation) => [
      translation.sourceTitle,
      {
        sourceTitle: translation.sourceTitle,
        fr: {
          title: translation.frTitle,
          descriptionBlocks: splitDescription(translation.frDescription),
        },
        ar: {
          title: translation.arTitle,
          descriptionBlocks: splitDescription(translation.arDescription),
        },
      },
    ]),
  );
}

function splitDescription(description: string) {
  return description
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
}

function findCopy(
  handle: string | undefined,
  sourceTitle: string,
  language: StorefrontLanguage,
): LocalizedProductCopy | undefined {
  if (language === 'en') return undefined;

  const productTranslation =
    (handle
      ? remoteTranslationsByHandle.get(handle) ?? productTranslations[handle]
      : undefined) ??
    remoteTranslationsBySourceTitle.get(sourceTitle) ??
    translationsBySourceTitle.get(sourceTitle);
  const copy = productTranslation?.[language as LocalizedProductLanguage];

  if (!copy && import.meta.env.DEV) {
    const missingKey = `${language}:${handle || sourceTitle}`;
    if (!warnedMissingTranslations.has(missingKey)) {
      warnedMissingTranslations.add(missingKey);
      console.warn(`[i18n] Missing app product translation: ${missingKey}`);
    }
  }

  return copy;
}

function escapeHtml(text: string) {
  return text.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return character;
    }
  });
}

export function getLocalizedProductTitle(
  handle: string | undefined,
  sourceTitle: string,
  language: StorefrontLanguage,
) {
  return findCopy(handle, sourceTitle, language)?.title ?? sourceTitle;
}

export function getLocalizedProductContent(
  product: {
    handle: string;
    title: string;
    description: string;
    descriptionHtml: string;
  },
  language: StorefrontLanguage,
) {
  const copy = findCopy(product.handle, product.title, language);
  if (!copy) {
    return {
      title: product.title,
      description: product.description,
      descriptionHtml: product.descriptionHtml,
    };
  }

  return {
    title: copy.title,
    description: copy.descriptionBlocks.join(' '),
    descriptionHtml: copy.descriptionBlocks
      .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
      .join('\n'),
  };
}

export function getLocalizedVariantTitle(
  sourceTitle: string | null | undefined,
  _language: StorefrontLanguage,
) {
  if (!sourceTitle || sourceTitle === 'Default Title') return '';
  return sourceTitle;
}